/**
 * Component Registry for Price Brothers Theme
 * 
 * Extends the base rise theme registry with Price Brothers specific overrides.
 */

import type { ComponentRegistry } from '@vibesites/runtime';
import { registry as baseRegistry } from '@vibesites/theme-rise/registry';
import { AccessibilityStatement } from './components/AccessibilityStatement';
import { AmenitiesList } from './components/AmenitiesList';
import { ApplyNowActions } from './components/ApplyNowActions';
import { ContactForm } from './components/ContactForm';
import { FaqList } from './components/FaqList';
import { FeaturedFloorPlan } from './components/FeaturedFloorPlan';
import { FloorplanShowcase } from './components/FloorplanShowcase';
import { FloorplansList } from './components/FloorplansList';
import { Footer } from './components/Footer';
import { GallerySeparator } from './components/GallerySeparator';
import { Header } from './components/Header';
import { Hero } from './components/Hero';
import { HeroSolidBG } from './components/HeroSolidBG';
import { HyperlocalHero } from './components/HyperlocalHero';
import { NeighborhoodMap } from './components/NeighborhoodMap';
import { PageBlurb } from './components/PageBlurb';
import { PepBenefits } from './components/PepBenefits';
import { PepCallToAction } from './components/PepCallToAction';
import { PepEmployers } from './components/PepEmployers';
import { PetPolicy } from './components/PetPolicy';
import { PhotoGallery } from './components/PhotoGallery';
import { PinnedCallouts } from './components/PinnedCallouts';
import { PropertyOverview } from './components/PropertyOverview';
import { Reviews } from './components/Reviews';
import { ScheduleTourForm } from './components/ScheduleTourForm';
import { SisterProperties } from './components/SisterProperties';
import { SitemapContent } from './components/SitemapContent';
import { SpecialOfferBanner } from './components/SpecialOfferBanner';
import { TabbedCallouts } from './components/TabbedCallouts';
import { ThankYouContent } from './components/ThankYouContent';
import { VirtualTourEmbed } from './components/VirtualTourEmbed';

/**
 * Price Brothers specific component overrides.
 * These components have been customized for the Price Brothers brand.
 */
const pricebrothersOverrides: Partial<ComponentRegistry> = {
  AccessibilityStatement,
  AmenitiesList,
  ApplyNowActions,
  ContactForm,
  FaqList,
  FeaturedFloorPlan,
  FloorplanShowcase,
  FloorplansList,
  Footer,
  GallerySeparator,
  Header,
  Hero,
  HeroSolidBG,
  HyperlocalHero,
  NeighborhoodMap,
  PageBlurb,
  PepBenefits,
  PepCallToAction,
  PepEmployers,
  PetPolicy,
  PhotoGallery,
  PinnedCallouts,
  PropertyOverview,
  Reviews,
  ScheduleTourForm,
  SisterProperties,
  SitemapContent,
  SpecialOfferBanner,
  TabbedCallouts,
  ThankYouContent,
  VirtualTourEmbed,
};

/**
 * Final merged registry for Price Brothers properties.
 * Inherits all components from the rise theme, with Price Brothers overrides.
 */
export const registry: ComponentRegistry = {
  ...baseRegistry,
  ...pricebrothersOverrides,
};

export default registry;
